import { plural } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, UserRound } from "lucide-react";
import ImportDialog, { ColumnNotes, Count, Decision, ProblemList, quoteList } from "@/components/ImportDialog";

/**
 * A training plan from a spreadsheet — one training per row, every one still to come.
 *
 * Asked for by Shakun HR on 4 Nov 2026: with the whole workforce on the register, the year's
 * trainings were the thing left to type into New training one at a time. Every imported
 * training arrives Scheduled, so it is on the dashboard's Upcoming list straight away; people
 * are nominated on each training afterwards, which notifies them.
 *
 * The flow is `ImportDialog`'s, shared with the employee import, so HR meets one flow twice.
 * What is here is the report: one decision (categories Settings does not have yet), and a list
 * of trainer names that will be saved as written — the one place a typo would otherwise pass.
 */
const ROUTES = {
  columns: "/trainings/import/columns",
  template: "/trainings/import/template.xlsx",
  preview: "/trainings/import/preview",
  commit: "/trainings/import",
};

export default function ImportTrainingsDialog({ open, onOpenChange, onImported }) {
  return (
    <ImportDialog
      open={open} onOpenChange={onOpenChange} onImported={onImported}
      title="Import trainings from Excel"
      description="One training per row, each on a date still to come. They arrive Scheduled; you nominate people on each one afterwards. You will see what the file contains before anything is saved."
      routes={ROUTES} templateName="trainings-import-template.xlsx"
      initialOptions={{ create: false }}
      formFields={(o) => ({ create_missing: o.create })}
      summary={(r) => `Sheet “${r.sheet}”, headers on row ${r.header_row} · ${plural(r.total_rows, "training")}`}
      confirmLabel={(r) => `Import ${plural(r.ready_count, "training")}`}
      successMessage={(d) => `Imported ${plural(d.created, "training")}, all Scheduled`
        + (d.categories_created ? ` · added ${plural(d.categories_created, "category", "categories")}` : "")}
      guideFootnote="the Trainings export from this register, with its dates changed, works too."
      renderReport={({ report, options, busy, toggle }) => (
        <Report report={report} create={options.create} busy={busy} onToggle={toggle} />
      )}
    />
  );
}

function Report({ report, create, busy, onToggle }) {
  const missing = report.missing_categories;
  const waiting = create ? 0 : missing.rows;

  return (
    <div className={`space-y-4 transition-opacity ${busy ? "opacity-60" : ""}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="import-counts">
        <Count n={report.ready_count} label="ready to import" tone="text-emerald-700 dark:text-emerald-400" />
        <Count n={report.already_on_file.length} label="already on the register" />
        <Count n={report.problems.length} label={report.problems.length === 1 ? "row needs fixing" : "rows need fixing"}
               tone={report.problems.length ? "text-destructive-text" : ""} />
        <Count n={waiting} label="waiting on Settings" tone={waiting ? "text-amber-700 dark:text-amber-400" : ""} />
      </div>

      <ColumnNotes report={report} />

      {missing.names.length > 0 && (
        <Decision icon={AlertTriangle} testid="import-categories"
                  title={`${plural(missing.names.length, "category", "categories")} not in Settings yet`}
                  body={quoteList(missing.names)}
                  control={<Checkbox checked={create} onCheckedChange={(v) => onToggle({ create: v === true })}
                                     disabled={busy} aria-label="Add them during this import" />}
                  controlLabel="Add them during this import"
                  footnote="Check the spelling first: each one joins the category list in Settings, just as if it were added there." />
      )}

      <ProblemList problems={report.problems} />

      {report.typed_trainers.length > 0 && (
        <div className="rounded-md border border-border p-3 flex gap-2" data-testid="import-typed-trainers">
          <UserRound className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground min-w-0">
            <span className="text-foreground font-medium">Not in Masters, saved as written: </span>
            {quoteList(report.typed_trainers)}. If one is a spelling of a trainer already in Masters,
            correct it in the file so their sessions count towards them.
          </p>
        </div>
      )}

      {report.ready_sample.length > 0 && (
        <div className="rounded-md border border-border" data-testid="import-sample">
          <div className="px-3 py-2 border-b border-border text-xs text-muted-foreground">
            How they will be added{report.ready_count > report.ready_sample.length &&
              ` — the first ${report.ready_sample.length} of ${report.ready_count}`}
          </div>
          {/* The date leads, because it is read day first and is the column most worth
              checking. On a phone Row and Hours fold away — hours ride under the trainer —
              so three columns fit 390px. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  {[["Row", "hidden sm:table-cell"], ["Date", ""], ["Training · Unit", ""],
                    ["Trainer", ""], ["Hours", "hidden sm:table-cell"]].map(([h, cls]) => (
                    <th key={h} className={`px-3 py-1.5 font-medium whitespace-nowrap ${cls}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {report.ready_sample.map((r) => (
                  <tr key={r.row} className="align-top">
                    <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground hidden sm:table-cell">{r.row}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.date}</td>
                    <td className="px-3 py-1.5 min-w-0">
                      <div className="break-words">{r.title}</div>
                      <div className="text-xs text-muted-foreground break-words">
                        {r.type} · {r.category} · {r.units}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="break-words">{r.trainer || "—"}</div>
                      <div className="text-xs text-muted-foreground sm:hidden">{plural(r.hours, "hr")}</div>
                    </td>
                    <td className="px-3 py-1.5 hidden sm:table-cell whitespace-nowrap">{r.hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {report.already_on_file.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="import-already">
          Left as they are, already on the register: {report.already_on_file.slice(0, 5)
            .map((a) => `${a.title} (${a.date})`).join(", ")}
          {report.already_on_file.length > 5 && ` and ${report.already_on_file.length - 5} more`}.
          A training with the same title, date and first unit is the same training; import never changes one.
        </p>
      )}
    </div>
  );
}
